<script lang="ts">
  import { t } from '$core/i18n';
  import {
    exportForeignQueue,
    moveForeignQueueToUser,
    discardForeignQueue,
    downloadJson,
    type ForeignQueueSummary
  } from '$features/sync';
  import { Modal } from '$ui/primitives/Modal';
  import { Button } from '$ui/primitives/Button';
  import { Checkbox } from '$ui/primitives/Checkbox';
  import { EmptyState } from '$ui/primitives/EmptyState';

  let {
    currentUserId,
    queues,
    onClose,
    onChanged
  }: {
    currentUserId: string;
    queues: ForeignQueueSummary[];
    onClose: () => void;
    onChanged: () => void;
  } = $props();

  let movingUserId = $state<string | null>(null);
  let moveConfirmed = $state(false);
  let discardUserId = $state<string | null>(null);
  let busy = $state(false);
  let message = $state<string | null>(null);
  let messageTimer: number | null = null;

  let movingQueue = $derived(
    movingUserId !== null
      ? queues.find((q) => q.userId === movingUserId) ?? null
      : null
  );

  function flash(text: string): void {
    message = text;
    if (messageTimer !== null) window.clearTimeout(messageTimer);
    messageTimer = window.setTimeout(() => {
      message = null;
      messageTimer = null;
    }, 4000);
  }

  function shortId(userId: string): string {
    return userId.length > 12 ? `${userId.slice(0, 8)}…` : userId;
  }

  function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  function totalCount(q: ForeignQueueSummary): number {
    return q.outboxCount + q.quarantineCount;
  }

  function totalBytes(q: ForeignQueueSummary): number {
    return q.outboxBytes + q.quarantineBytes;
  }

  async function handleExport(q: ForeignQueueSummary): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      const data = await exportForeignQueue(q.userId);
      const shortId = q.userId.slice(0, 8);
      const date = new Date().toISOString().slice(0, 10);
      downloadJson(`studyquantifier-queue-${shortId}-${date}.json`, data);
      flash(t('foreignQueues.flashExported'));
    } catch (err) {
      console.error('[StudyQuantifier] export foreign queue failed:', err);
    } finally {
      busy = false;
    }
  }

  function startMove(q: ForeignQueueSummary): void {
    movingUserId = q.userId;
    moveConfirmed = false;
  }

  async function confirmMove(): Promise<void> {
    if (!movingUserId || !moveConfirmed || busy) return;
    busy = true;
    try {
      const result = await moveForeignQueueToUser(movingUserId, currentUserId);
      const n = result.outboxMoved + result.quarantineMoved;
      flash(t('foreignQueues.flashMoved', { n: String(n) }));
      movingUserId = null;
      moveConfirmed = false;
      onChanged();
    } catch (err) {
      console.error('[StudyQuantifier] move foreign queue failed:', err);
    } finally {
      busy = false;
    }
  }

  function cancelMove(): void {
    movingUserId = null;
    moveConfirmed = false;
  }

  function startDiscard(userId: string): void {
    discardUserId = userId;
  }

  function cancelDiscard(): void {
    discardUserId = null;
  }

  async function confirmDiscard(): Promise<void> {
    if (!discardUserId || busy) return;
    busy = true;
    try {
      const result = await discardForeignQueue(discardUserId);
      const n = result.outboxDiscarded + result.quarantineDiscarded;
      flash(t('foreignQueues.flashDiscarded', { n: String(n) }));
      discardUserId = null;
      onChanged();
    } catch (err) {
      console.error('[StudyQuantifier] discard foreign queue failed:', err);
    } finally {
      busy = false;
    }
  }

  let mainOpen = $state(true);
  let moveOpen = $derived(movingQueue !== null);

  function handleMainClose() {
    onClose();
  }

  function handleMoveClose() {
    cancelMove();
  }
</script>

<Modal
  bind:open={mainOpen}
  title={t('foreignQueues.title')}
  size="lg"
  onclose={handleMainClose}
>
  <div class="body">
    <p class="intro">
      {t('foreignQueues.intro', { n: String(queues.length) })}
    </p>

    {#if message}
      <div class="flash" role="status">{message}</div>
    {/if}

    {#if queues.length === 0}
      <EmptyState
        icon="inbox"
        title={t('foreignQueues.empty')}
        description={t('foreignQueues.emptyHint')}
      />
    {:else}
      <ul class="list">
        {#each queues as q (q.userId)}
          <li class="row">
            <div class="row-info">
              <div class="row-id">{shortId(q.userId)}</div>
              <div class="row-meta">
                <span>{totalCount(q)} {t('foreignQueues.opsLabel')}</span>
                <span class="dot">·</span>
                <span>{formatBytes(totalBytes(q))}</span>
              </div>
            </div>

            {#if discardUserId === q.userId}
              <div class="confirm-discard">
                <span class="confirm-text">
                  {t('foreignQueues.confirmDiscardInline', {
                    n: String(totalCount(q)),
                    bytes: formatBytes(totalBytes(q))
                  })}
                </span>
                <Button variant="danger" size="sm" disabled={busy} onclick={confirmDiscard}>
                  {t('foreignQueues.yesDiscard')}
                </Button>
                <Button variant="outline" size="sm" disabled={busy} onclick={cancelDiscard}>
                  {t('foreignQueues.no')}
                </Button>
              </div>
            {:else}
              <div class="row-actions">
                <Button variant="outline" size="sm" disabled={busy} onclick={() => handleExport(q)}>
                  {t('foreignQueues.export')}
                </Button>
                <Button variant="outline" size="sm" disabled={busy} onclick={() => startMove(q)}>
                  {t('foreignQueues.move')}
                </Button>
                <Button variant="danger" size="sm" disabled={busy} onclick={() => startDiscard(q.userId)}>
                  {t('foreignQueues.discard')}
                </Button>
              </div>
            {/if}
          </li>
        {/each}
      </ul>
    {/if}
  </div>

  {#snippet footer()}
    <Button variant="outline" disabled={busy} onclick={onClose}>
      {t('foreignQueues.close')}
    </Button>
  {/snippet}
</Modal>

{#if moveOpen && movingQueue}
  <Modal
    bind:open={moveOpen}
    title={t('foreignQueues.moveTitle')}
    size="sm"
    onclose={handleMoveClose}
  >
    <div class="body">
      <p class="sub-body">
        {t('foreignQueues.moveBody', {
          n: String(totalCount(movingQueue)),
          bytes: formatBytes(totalBytes(movingQueue))
        })}
      </p>
      <Checkbox bind:checked={moveConfirmed} label={t('foreignQueues.moveAck')} />
    </div>

    {#snippet footer()}
      <Button variant="outline" disabled={busy} onclick={cancelMove}>
        {t('foreignQueues.cancel')}
      </Button>
      <Button variant="filled" disabled={busy || !moveConfirmed} onclick={confirmMove}>
        {t('foreignQueues.moveConfirm')}
      </Button>
    {/snippet}
  </Modal>
{/if}

<style>
  .body {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .intro {
    margin: 0;
    color: var(--color-text-secondary);
    font-size: var(--text-sm);
  }

  .flash {
    padding: var(--space-3);
    background: var(--color-accent-tint);
    border-left: 3px solid var(--color-accent);
    border-radius: var(--radius-md);
    color: var(--color-text-primary);
    font-size: var(--text-sm);
  }

  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    padding: var(--space-3);
    min-height: 64px;
    background: var(--color-surface-sunken);
    border-radius: var(--radius-md);
  }

  .row-info {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
    flex: 1;
  }

  .row-id {
    font-family: var(--font-mono);
    font-size: var(--text-body);
    color: var(--color-text-primary);
  }

  .row-meta {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-caption);
    color: var(--color-text-muted);
  }

  .dot {
    opacity: 0.5;
  }

  .row-actions {
    display: flex;
    gap: var(--space-2);
    flex: 0 0 auto;
  }

  .confirm-discard {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .confirm-text {
    font-size: var(--text-caption);
    color: var(--color-danger);
  }

  .sub-body {
    margin: 0;
    font-size: var(--text-body);
    color: var(--color-text-secondary);
    line-height: var(--text-body-lh);
    text-wrap: pretty;
  }
</style>